import React, { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient, ScanCommand } from "@aws-sdk/lib-dynamodb";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import awsconfig from '../aws-exports';
import './Dashboard.css';

function Dashboard() {
  const [rsvpData, setRsvpData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpStats, setRsvpStats] = useState({
    total: 0,
    yes: 0,
    no: 0,
    notYet: 0
  });
  const [statusStats, setStatusStats] = useState({
    'Save The Date Received': 0,
    'RSVP sent': 0,
    'RSVPed': 0,
    'Other': 0
  });

  const [mealChoiceStats, setMealChoiceStats] = useState({
    'beef_salmon': 0,
    'vegetarian': 0,
    'kids_meal': 0,
    'not_specified': 0
  });

  async function fetchRsvpData() {
    setIsLoading(true);
    try {
      // Get credentials from Amplify Auth
      const currentSession = await Amplify.Auth.currentSession();
      const idToken = currentSession.getIdToken().getJwtToken();
      
      // Configure credential provider
      const region = awsconfig.aws_project_region;
      const identityPoolId = awsconfig.aws_cognito_identity_pool_id;
      
      // Create credentials provider
      const loginKey = `cognito-idp.${region}.amazonaws.com/${awsconfig.aws_user_pools_id}`;
      const logins = {};
      logins[loginKey] = idToken;
      
      const credentialsProvider = fromCognitoIdentityPool({
        client: new CognitoIdentityClient({ region }),
        identityPoolId: identityPoolId,
        logins: logins
      });

      // Create DynamoDB client
      const dynamoClient = new DynamoDBClient({
        region,
        credentials: credentialsProvider
      });
      
      // Create DynamoDB Document client
      const docClient = DynamoDBDocumentClient.from(dynamoClient, {
        marshallOptions: { removeUndefinedValues: true }
      });
      
      // Create scan command
      const tableName = 'erikaandrew_wedding';
      const scanCommand = new ScanCommand({
        TableName: tableName,
        ConsistentRead: true
      });
      
      // Execute scan command
      const result = await docClient.send(scanCommand);
      setRsvpData(result.Items || []);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching RSVP data:', err);
      setError(`Failed to load RSVP data: ${err.message}`);
      setIsLoading(false);
    }
  }

  

  const exportPrimaryContacts = useCallback(() => {
    // First, find all primary contacts
    const primaryContacts = rsvpData.filter(item => item.isPrimary === true);
    
    if (primaryContacts.length === 0) {
      alert("No primary contacts found to export");
      return;
    }

    if (primaryContacts.length > 0) {
      const firstPrimary = primaryContacts[0];
      console.log('First primary contact ID:', firstPrimary.id);
      console.log('First primary contact name:', `${firstPrimary.firstName || ''} ${firstPrimary.lastName || ''}`);
      console.log('Group field type:', typeof firstPrimary.group);
      console.log('Group field value:', firstPrimary.group);
      
      if (firstPrimary.group) {
        console.log('Group field constructor:', firstPrimary.group.constructor && firstPrimary.group.constructor.name);
        console.log('Group field is array:', Array.isArray(firstPrimary.group));
        console.log('Group field is Set:', firstPrimary.group instanceof Set);
        console.log('Group field has type property:', firstPrimary.group.type);
        
        if (firstPrimary.group.values) {
          console.log('Group values property:', firstPrimary.group.values);
        }
      }
    }
    
    // Create a map of all contacts for quick lookup when processing group members
    const allContactsMap = rsvpData.reduce((map, contact) => {
      if (contact.id) {
        map[contact.id.toLowerCase()] = contact;
      }
      return map;
    }, {});
    
    // Array to store all our export data
    const exportData = [];
    
    // Process each primary contact
    primaryContacts.forEach(primary => {
      // Add primary contact to export list
      const primaryExport = {
        firstName: primary.firstName || '',
        lastName: primary.lastName || '',
        email: primary.id || '',
        address_street: primary.address_street || '',
        address_city: primary.address_city || '',
        address_state: primary.address_state || '',
        address_zip: primary.address_zip || '',
        isPrimary: 'Yes',
        // Add names of all group members in a comma-separated list
        memberNames: getGroupMemberNames(primary, allContactsMap),
        // groupNames: primary.group
        groupNames: (() => {
          const primaryName = `${primary.firstName || ''} ${primary.lastName || ''}`.trim();
          const groupMemberNames = getGroupMemberNames(primary, allContactsMap);
          
          if (!groupMemberNames) {
            return primaryName;
          }
          
          // If there are group members, format properly with "and"
          if (groupMemberNames.includes(' and ')) {
            // Already formatted with "and" - just combine properly
            return `${primaryName}, ${groupMemberNames}`;
          } else {
            // Single member - simple format
            return `${primaryName} and ${groupMemberNames}`;
          }
        })()

      };
      exportData.push(primaryExport);
    });
      
      // Add this helper function to get group member names before the exportPrimaryContacts function
      function getGroupMemberNames(primary, contactsMap) {

            // Debug the input
        console.log(`=== Processing ${primary.id} (${primary.firstName} ${primary.lastName}) ===`);
        console.log('Group input:', primary.group);
    
        // If no group, return empty string
        if (!primary.group) {
          return '';
        }
        
        let groupEmails = [];
        
        // Handle DynamoDB Set objects
        // if (primary.group.type === 'Set' && primary.group.values) {
        //   groupEmails = primary.group.values;
        // } 
          // Handle standard JavaScript Set
        if (typeof primary.group === 'object' && primary.group.constructor && primary.group.constructor.name === 'Set') {
          console.log('Processing as standard JavaScript Set');
          try {
            // Use Array.from or spread operator to convert Set to array
            groupEmails = [...primary.group];
            console.log('Converted Set to array using spread:', groupEmails);
          } catch (e) {
            console.error('Error spreading Set:', e);
            // Alternative method
            groupEmails = Array.from(primary.group);
            console.log('Converted Set using Array.from:', groupEmails);
          }
        }
        // // Handle regular arrays
        // else if (Array.isArray(primary.group)) {
        //   groupEmails = primary.group.filter(email => email && email.trim() !== '');
        // } 
        // // Handle when group is already a plain JS Set
        // else if (primary.group instanceof Set) {
        //   groupEmails = Array.from(primary.group);
        // }
        
        // If no emails after conversion, return empty string
        if (groupEmails.length === 0) {
          return '';
        }
        
        // Map emails to names and filter out any empty ones
        const groupNames = groupEmails
          .map(email => {
            if (!email) return '';
            
            const memberDetails = contactsMap[email.toLowerCase()];
            if (memberDetails && (memberDetails.firstName || memberDetails.lastName)) {
              return `${memberDetails.firstName || ''} ${memberDetails.lastName || ''}`.trim();
            }
            return email; // Use email if name not found
          })
          .filter(name => name !== '');
        
        // Add debug logging
        console.log(`Group for ${primary.id}:`, primary.group);
        console.log(`Converted group emails:`, groupEmails);
        console.log(`Group names:`, groupNames);
        
          // Format names with "and" and Oxford comma if needed
        if (groupNames.length === 0) {
          return '';
        } else if (groupNames.length === 1) {
          return groupNames[0];
        } else if (groupNames.length === 2) {
          return `${groupNames[0]} and ${groupNames[1]}`;
        } else {
          // For 3 or more names, use Oxford comma: "A, B, and C"
          const lastItem = groupNames.pop();
          return `${groupNames.join(', ')}, and ${lastItem}`;
        }
      }

      // Logging for debugging
    console.log(`Found ${primaryContacts.length} primary contacts`);
    console.log(`Prepared ${exportData.length} contacts for export`);
  
    
    // Convert to CSV
    const headers = [
      "First Name", 
      "Last Name", 
      "Email",
      "Street Address", 
      "City", 
      "State", 
      "ZIP Code", 
      "Primary Contact",
      "Group Members",
      "Group Names"
    ];
    
    let csvContent = headers.join(",") + "\r\n";
    
    exportData.forEach(item => {
      // Properly escape fields for CSV format
      const row = [
        `"${(item.firstName || '').replace(/"/g, '""')}"`,
        `"${(item.lastName || '').replace(/"/g, '""')}"`,
        `"${(item.email || '').replace(/"/g, '""')}"`,
        `"${(item.address_street || '').replace(/"/g, '""')}"`,
        `"${(item.address_city || '').replace(/"/g, '""')}"`,
        `"${(item.address_state || '').replace(/"/g, '""')}"`,
        `"${(item.address_zip || '').replace(/"/g, '""')}"`,
        `"${item.isPrimary}"`,
        `"${(item.memberNames ? String(item.groupNames) : '').replace(/"/g, '""')}"`,
        `"${(item.groupNames ? String(item.groupNames) : '').replace(/"/g, '""')}"`
      ];
      
      csvContent += row.join(",") + "\r\n";
    });
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    
    link.setAttribute("href", url);
    link.setAttribute("download", `wedding-contacts-${timestamp}.csv`);
    link.style.display = "none";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rsvpData]);

  const calculateStats = useCallback(() => {
    // Calculate RSVP stats
    const rsvpCounts = {
      total: rsvpData.length,
      yes: 0,
      no: 0,
      notYet: 0
    };

    // Calculate status stats
    const statusCounts = {
      'Save the Date Received': 0,
      'RSVP sent': 0,
      'RSVPed': 0,
      'Other': 0
    };

    const mealCounts = {
      'beef_salmon': 0,
      'vegetarian': 0,
      'kids_meal': 0,
      'not_specified': 0
    };

    rsvpData.forEach(item => {
      // Count RSVP status
      if (item.rsvp === "attending") {
        rsvpCounts.yes += 1;
        if (item.mealChoice === 'beef_salmon') {
          mealCounts['beef_salmon'] += 1;
        } else if (item.mealChoice === 'vegetarian') {
          mealCounts['vegetarian'] += 1;
        } else if (item.mealChoice === 'kids_meal') {
          mealCounts['kids_meal'] += 1;
        } else {
          mealCounts['not_specified'] += 1;
        }
      } else if (item.cannot_attend === true) {
        rsvpCounts.no += 1;
      } else {
        rsvpCounts.notYet += 1;
      }

      // Count by specific status values
      if (item.status) {
        const status = item.status.trim();
        if (status === 'Save the Date Received' || status === 'RSVP sent' || status === 'RSVPed') {
          statusCounts[status] += 1;
        } else {
          statusCounts['Other'] += 1;
        }
      } else {
        statusCounts['Other'] += 1;
      }
    });

    setRsvpStats(rsvpCounts);
    setStatusStats(statusCounts);
    setMealChoiceStats(mealCounts);
  }, [rsvpData]);

  useEffect(() => {
    fetchRsvpData();
  }, []);

  useEffect(() => {
    if (rsvpData.length > 0) {
      calculateStats();
    }
  }, [rsvpData, calculateStats]);

  if (isLoading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <h1>Wedding RSVP Dashboard</h1>
      
      <div className="dashboard-section">
        <h2>RSVP Statistics</h2>
        <div className="stats-card-container">
          <div className="stats-card total">
            <h3>Total Guests</h3>
            <div className="stat-value">{rsvpStats.total}</div>
          </div>
          <div className="stats-card attending">
            <h3>Attending</h3>
            <div className="stat-value">{rsvpStats.yes}</div>
            <div className="stat-percentage">
              {rsvpStats.total > 0 ? Math.round((rsvpStats.yes / rsvpStats.total) * 100) : 0}%
            </div>
          </div>
          <div className="stats-card declined">
            <h3>Declined</h3>
            <div className="stat-value">{rsvpStats.no}</div>
            <div className="stat-percentage">
              {rsvpStats.total > 0 ? Math.round((rsvpStats.no / rsvpStats.total) * 100) : 0}%
            </div>
          </div>
          <div className="stats-card pending">
            <h3>Pending</h3>
            <div className="stat-value">{rsvpStats.notYet}</div>
            <div className="stat-percentage">
              {rsvpStats.total > 0 ? Math.round((rsvpStats.notYet / rsvpStats.total) * 100) : 0}%
            </div>
          </div>
        </div>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>RSVP Status</th>
              <th>Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Attending</td>
              <td>{rsvpStats.yes}</td>
              <td>{rsvpStats.total > 0 ? Math.round((rsvpStats.yes / rsvpStats.total) * 100) : 0}%</td>
            </tr>
            <tr>
              <td>Declined</td>
              <td>{rsvpStats.no}</td>
              <td>{rsvpStats.total > 0 ? Math.round((rsvpStats.no / rsvpStats.total) * 100) : 0}%</td>
            </tr>
            <tr>
              <td>Pending</td>
              <td>{rsvpStats.notYet}</td>
              <td>{rsvpStats.total > 0 ? Math.round((rsvpStats.notYet / rsvpStats.total) * 100) : 0}%</td>
            </tr>
            <tr className="total-row">
              <td>Total</td>
              <td>{rsvpStats.total}</td>
              <td>100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="dashboard-section">
        <h2>Invitation Status</h2>
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(statusStats).map(([status, count]) => (
              <tr key={status}>
                <td>{status}</td>
                <td>{count}</td>
                <td>{rsvpStats.total > 0 ? Math.round((count / rsvpStats.total) * 100) : 0}%</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>Total</td>
              <td>{rsvpStats.total}</td>
              <td>100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="dashboard-section">
        <h2>Meal Choices</h2>
        <div className="stats-card-container">
          <div className="stats-card meal-surf-turf">
            <h3>Surf and Turf</h3>
            <div className="stat-value">{mealChoiceStats['beef_salmon']}</div>
            <div className="stat-percentage">
              {rsvpStats.yes > 0 ? Math.round((mealChoiceStats['beef_salmon'] / rsvpStats.yes) * 100) : 0}%
            </div>
          </div>
          <div className="stats-card meal-vegetarian">
            <h3>Vegetarian</h3>
            <div className="stat-value">{mealChoiceStats['vegetarian']}</div>
            <div className="stat-percentage">
              {rsvpStats.yes > 0 ? Math.round((mealChoiceStats['vegetarian'] / rsvpStats.yes) * 100) : 0}%
            </div>
          </div>
          <div className="stats-card meal-kids">
            <h3>Kids Meal</h3>
            <div className="stat-value">{mealChoiceStats['kids_meal']}</div>
            <div className="stat-percentage">
              {rsvpStats.yes > 0 ? Math.round((mealChoiceStats['kids_meal'] / rsvpStats.yes) * 100) : 0}%
            </div>
          </div>
          <div className="stats-card meal-unknown">
            <h3>Not Specified</h3>
            <div className="stat-value">{mealChoiceStats['not_specified']}</div>
            <div className="stat-percentage">
              {rsvpStats.yes > 0 ? Math.round((mealChoiceStats['not_specified'] / rsvpStats.yes) * 100) : 0}%
            </div>
          </div>
        </div>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Meal Choice</th>
              <th>Count</th>
              <th>Percentage of Attending</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Surf and Turf</td>
              <td>{mealChoiceStats['beef_salmon']}</td>
              <td>{rsvpStats.yes > 0 ? Math.round((mealChoiceStats['beef_salmon'] / rsvpStats.yes) * 100) : 0}%</td>
            </tr>
            <tr>
              <td>Vegetarian</td>
              <td>{mealChoiceStats['vegetarian']}</td>
              <td>{rsvpStats.yes > 0 ? Math.round((mealChoiceStats['vegetarian'] / rsvpStats.yes) * 100) : 0}%</td>
            </tr>
            <tr>
              <td>Kids Meal</td>
              <td>{mealChoiceStats['kids_meal']}</td>
              <td>{rsvpStats.yes > 0 ? Math.round((mealChoiceStats['kids_meal'] / rsvpStats.yes) * 100) : 0}%</td>
            </tr>
            <tr>
              <td>Not Specified</td>
              <td>{mealChoiceStats['not_specified']}</td>
              <td>{rsvpStats.yes > 0 ? Math.round((mealChoiceStats['not_specified'] / rsvpStats.yes) * 100) : 0}%</td>
            </tr>
            <tr className="total-row">
              <td>Total Attending</td>
              <td>{rsvpStats.yes}</td>
              <td>100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="dashboard-footer">
        <div className="dashboard-actions">
          <button onClick={fetchRsvpData} className="refresh-button">
            Refresh Data
          </button>
          <button onClick={exportPrimaryContacts} className="export-button">
            Export Contact List
          </button>
        </div>
        <p className="last-updated">
          Last updated: {new Date().toLocaleString()}
        </p>
      </div>
    </div>
    
  );
}

export default Dashboard;